import React, { ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MailCheck } from 'lucide-react';
import { SprintProductStatus } from '@data';
import { useListPmSprintProducts } from '@data/hooks/sprintProducts';
import { insightsOverviewPath, productReportingPath } from '@app/constants/url';
import { useViewTransitionNav } from '@app/components/core/viewTransition';
import { useOrganizationId } from '@app/utils/hooks';
import { ListCard, ListRow, RowBadge } from './ListCard';

// The click opens the newest of the product's waiting reports, so the row carries that pair.
type ProductRow = ListRow & { sprintId: string; productId: string };

/**
 * Reports waiting on a client's approval, grouped per product.
 *
 * The card the PRD opens on, rebuilt. The one it replaces asked the **client's** question — which reports
 * were sent to me? — so a manager was not allowed to read it, the refusal was swallowed, and it announced
 * "all approved" while closed sprints piled up. This one asks the organization's question, and a failed read
 * says so rather than reading as an all-clear.
 *
 * Three narrowings, all of them load-bearing: closed, actually sent, and with time in it. A report nobody
 * sent is waiting on Panenco rather than on the client, and an empty report has nothing to approve.
 *
 * Grouped per product with a count, so a product with a backlog reads as one row rather than filling the
 * card and burying the others.
 */
export const AwaitingApprovalCard = (): ReactElement => {
  const { t } = useTranslation();
  const transitionNavigate = useViewTransitionNav();
  const organizationId = useOrganizationId() ?? '';

  const params = useMemo(() => ({ statuses: [SprintProductStatus.closed], sent: true, hasReportedTime: true }), []);
  const { data, isError } = useListPmSprintProducts(organizationId, params);

  const rows = useMemo<ProductRow[]>(() => {
    const byProduct = new Map<string, { name: string; sprintId: string; count: number }>();

    // The read comes back newest sprint first, so the first one seen per product is the one to open.
    (data?.items ?? []).forEach(item => {
      const productId = item.product?.id ?? item.productId;
      const sprintId = item.sprint?.id ?? item.sprintId;
      if (!productId || !sprintId) return;

      const existing = byProduct.get(productId);
      if (existing) {
        existing.count += 1;
        return;
      }
      byProduct.set(productId, { name: item.product?.name ?? '—', sprintId, count: 1 });
    });

    return (
      [...byProduct.entries()]
        .map(([productId, group]) => ({
          id: productId,
          productId,
          sprintId: group.sprintId,
          name: group.name,
          // The figure is what the reader acts on, so it goes in the badge rather than beside the name.
          badge: group.count > 1 ? <RowBadge>{group.count}</RowBadge> : undefined,
          count: group.count,
        }))
        // Biggest backlog first: a product with five reports waiting is the one to chase.
        .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
    );
  }, [data]);

  return (
    <ListCard
      icon={MailCheck}
      title={t('awaitingApproval')}
      rows={rows}
      empty={t('nothingAwaitingApproval')}
      failed={isError}
      // The newest waiting report of that product, which is where its approval state is shown.
      onOpen={row =>
        transitionNavigate(productReportingPath(organizationId, row.productId, row.sprintId), 'forward', {
          state: { backPath: insightsOverviewPath(organizationId), backLabel: t('actions.backToInsights') },
        })
      }
    />
  );
};
